import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { usePreventBack } from '../../hooks/usePreventBack';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import {
  MicrophoneIcon,
  SpeakerWaveIcon,
  VideoCameraIcon,
  BellIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Room, createLocalAudioTrack } from 'livekit-client';
import { API_BASE_URL } from '../../lib/axiosConfig';
import logger from '../../utils/logger';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import { useCandidateLanguage } from '../../i18n/candidate';

/**
 * Pre-Interview Audio/Video Test Page
 * Matches Ribbon.ai design - allows users to test audio/video before starting interview
 * Video settings marked as "Coming Soon" - camera dropdown navigates to privacy policy page
 */
const AIInterviewPreTest = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { sharingLink: sharingLinkParam } = useParams();
  const sharingLink = sharingLinkParam || state?.sharingLink;
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  usePreventBack(!!state?.screeningId, { onBackAttempt: useCallback(() => setShowLeaveModal(true), []) });
  useScreeningLeaveWarning(!!state?.screeningId);
  const {
    name,
    email,
    interview,
    sessionId,
    sessionToken,
    opening,
    maxQuestions,
    voiceModel,
    useEnhancedAI,
    deliveryMode,
  } = state || {};
  const t = useCandidateLanguage({ screeningId: state?.screeningId, interviewId: interview?._id });

  // Audio device state
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [inputLevel, setInputLevel] = useState(0);
  const [isTestingSpeaker, setIsTestingSpeaker] = useState(false);
  const [testAudioBlob, setTestAudioBlob] = useState(null);

  // Video device state
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [cameraPermissionGranted, setCameraPermissionGranted] = useState(false);
  const cameraStreamRef = useRef(null);
  const cameraPreviewRef = useRef(null);

  const interviewType = interview?.type || 'voice';
  const isVideoInterview = interviewType === 'video';

  // Livekit initialization state
  const [isInitializingLivekit, setIsInitializingLivekit] = useState(false);
  const [livekitError, setLivekitError] = useState(null);

  // Capacity state — only triggered by a CAPACITY_REACHED response from the server
  const [capacityAvailable, setCapacityAvailable] = useState(true);
  const capacityRetryRef = useRef(null);

  // Early agent warm-up: prepared session from POST /session/prepare (called on mount)
  const [preparedSession, setPreparedSession] = useState(null);
  const prepareCalledRef = useRef(false);

  // Refs
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const testAudioRef = useRef(null);
  const roomRef = useRef(null);
  const micTrackRef = useRef(null);

  /**
   * Load available audio/video devices
   */
  const loadDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioInputs = devices.filter((d) => d.kind === 'audioinput');
      const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');
      const videos = devices.filter((d) => d.kind === 'videoinput');

      setAudioInputDevices(audioInputs);
      setAudioOutputDevices(audioOutputs);
      setVideoDevices(videos);

      // Set defaults
      if (audioInputs.length > 0 && !selectedMicrophone) {
        const defaultMic = audioInputs.find((d) => d.deviceId === 'default') || audioInputs[0];
        setSelectedMicrophone(defaultMic.deviceId);
      }
      if (audioOutputs.length > 0 && !selectedSpeaker) {
        const defaultSpeaker =
          audioOutputs.find((d) => d.deviceId === 'default') || audioOutputs[0];
        setSelectedSpeaker(defaultSpeaker.deviceId);
      }
      if (videos.length > 0 && !selectedCamera) {
        const defaultCam = videos.find((d) => d.deviceId === 'default') || videos[0];
        setSelectedCamera(defaultCam.deviceId);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  }, [selectedMicrophone, selectedSpeaker, selectedCamera]);

  /**
   * Start microphone input level monitoring
   */
  const startInputLevelMonitoring = useCallback(async () => {
    try {
      // Stop existing stream if any
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Request microphone access with selected device
      const constraints = {
        audio:
          selectedMicrophone && selectedMicrophone !== 'default'
            ? { deviceId: { exact: selectedMicrophone } }
            : true,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      micStreamRef.current = stream;

      // Set up audio analysis
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start monitoring
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setInputLevel(average / 255); // Normalize to 0-1
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setLivekitError('Microphone access denied. Please allow microphone permissions.');
    }
  }, [selectedMicrophone]);

  /**
   * Stop microphone monitoring
   */
  const stopInputLevelMonitoring = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setInputLevel(0);
  }, []);

  /**
   * Test speaker playback
   */
  const testSpeaker = useCallback(async () => {
    if (isTestingSpeaker) return;

    try {
      setIsTestingSpeaker(true);

      // Record 2 seconds of audio
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setTestAudioBlob(blob);

        // Play back the recorded audio
        const audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(audioUrl);

        // Set output device if selected
        if (selectedSpeaker && 'setSinkId' in audio) {
          try {
            await audio.setSinkId(selectedSpeaker);
          } catch (err) {
            console.warn('Could not set audio output device:', err);
          }
        }

        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          setIsTestingSpeaker(false);
        };

        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          setIsTestingSpeaker(false);
        };

        await audio.play();
        testAudioRef.current = audio;
      };

      mediaRecorder.start();
      await new Promise((resolve) => setTimeout(resolve, 2000));
      mediaRecorder.stop();
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error('Error testing speaker:', error);
      setIsTestingSpeaker(false);
    }
  }, [selectedSpeaker, isTestingSpeaker]);

  /**
   * Initialize Livekit connection in background
   */
  const initializeLivekit = useCallback(async () => {
    if (isInitializingLivekit || roomRef.current) return;

    setIsInitializingLivekit(true);
    setLivekitError(null);

    // Use prepared session if available (agent already dispatched); else use state
    const effectiveSessionId = preparedSession?.sessionId ?? sessionId;
    const effectiveSessionToken = preparedSession?.sessionToken ?? sessionToken;

    try {
      // Get LiveKit token (JWT only when prepare already ran — no dispatch delay)
      const tokenHeaders = { 'Content-Type': 'application/json' };
      if (effectiveSessionToken) tokenHeaders['x-session-token'] = effectiveSessionToken;

      const tokenRes = await fetch(`${API_BASE_URL}/api/ai-interviews/session/livekit-token`, {
        method: 'POST',
        headers: tokenHeaders,
        body: JSON.stringify({
          sharingLink,
          candidateName: name,
          candidateEmail: email,
          sessionId: effectiveSessionId,
        }),
      });

      if (!tokenRes.ok) {
        let errorData;
        try {
          errorData = await tokenRes.json();
        } catch {
          throw new Error(`Failed to get LiveKit token (${tokenRes.status})`);
        }

        // Handle capacity error — LiveKit rejected the dispatch (concurrency limit hit)
        if (errorData.code === 'CAPACITY_REACHED') {
          setCapacityAvailable(false);
          setLivekitError(null);
          setIsInitializingLivekit(false);
          // Re-enable the button after 30 seconds so the user can retry
          if (capacityRetryRef.current) clearTimeout(capacityRetryRef.current);
          capacityRetryRef.current = setTimeout(() => {
            setCapacityAvailable(true);
          }, 30000);
          return;
        }

        throw new Error(errorData.error || errorData.message || 'Failed to get LiveKit token');
      }

      const tokenData = await tokenRes.json();
      const { token, url } = tokenData;

      if (!url || !url.startsWith('wss://')) {
        throw new Error('Invalid LiveKit URL format');
      }

      // Clean URL (remove /rtc or /ws if present)
      let cleanUrl = url.trim();
      if (cleanUrl.endsWith('/rtc') || cleanUrl.endsWith('/rtc/')) {
        cleanUrl = cleanUrl.replace(/\/rtc\/?$/, '');
      }
      if (cleanUrl.endsWith('/ws') || cleanUrl.endsWith('/ws/')) {
        cleanUrl = cleanUrl.replace(/\/ws\/?$/, '');
      }

      // Create microphone track with selected device (for testing/preview)
      const micConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
      };

      if (selectedMicrophone && selectedMicrophone !== 'default') {
        micConstraints.deviceId = { exact: selectedMicrophone };
      }

      // Navigate directly to interview page (no waiting page, no countdown)
      // Pass pre-obtained Livekit credentials for faster connection
      logger.log('✅ Livekit token obtained successfully');
      setIsInitializingLivekit(false);

      // Report device permissions status to server for logging/auditing
      const effectiveSid = preparedSession?.sessionId ?? sessionId;
      const effectiveInterviewId = interview?._id;
      if (effectiveSid && effectiveInterviewId) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const micDevices = devices.filter((d) => d.kind === 'audioinput');
          const camDevices = devices.filter((d) => d.kind === 'videoinput');
          // Check if we have real labels (indicates permission was granted)
          const micGranted = micDevices.some((d) => d.label && d.label.length > 0);
          const camGranted = camDevices.some((d) => d.label && d.label.length > 0);
          fetch(`${API_BASE_URL}/api/analytics/events`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId: effectiveSid,
              interviewId: effectiveInterviewId,
              events: [{
                eventType: 'DEVICE_PERMISSIONS_CHECK',
                timestamp: new Date().toISOString(),
                microphonePermission: micGranted ? 'granted' : (micDevices.length > 0 ? 'denied' : 'no_device'),
                cameraPermission: isVideoInterview ? (camGranted ? 'granted' : (camDevices.length > 0 ? 'denied' : 'no_device')) : 'not_required',
                microphoneDevicesCount: micDevices.length,
                cameraDevicesCount: camDevices.length,
                interviewType: interviewType,
                userAgent: navigator.userAgent,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
              }],
            }),
          }).catch(() => { /* best-effort */ });
        } catch { /* best-effort */ }
      }

      // Use prepared session data when available (from early warm-up)
      const effectiveMaxQuestions = preparedSession?.maxQuestions ?? maxQuestions;
      const effectiveVoiceModel = preparedSession?.voiceModel ?? voiceModel;
      const effectiveUseEnhancedAI = preparedSession?.useEnhancedAI ?? useEnhancedAI;
      const effectiveDeliveryMode = preparedSession?.deliveryMode ?? deliveryMode;

      // Navigate directly to interview page
      navigate(`/ai-interview/${sharingLink}/take`, {
        state: {
          sharingLink,
          name,
          email,
          interview,
          sessionId: effectiveSessionId,
          sessionToken: effectiveSessionToken,
          opening,
          maxQuestions: effectiveMaxQuestions,
          voiceModel: effectiveVoiceModel,
          useEnhancedAI: effectiveUseEnhancedAI,
          deliveryMode: effectiveDeliveryMode,
          // Pass pre-obtained Livekit credentials
          livekitUrl: cleanUrl,
          livekitToken: token,
          selectedMicrophoneId: selectedMicrophone, // Pass selected device ID
          preInitializedLivekit: true, // Flag to indicate credentials are pre-obtained
        },
      });
    } catch (error) {
      console.error('Error initializing Livekit:', error);
      setLivekitError(error.message || 'Failed to initialize interview connection');
      setIsInitializingLivekit(false);
    }
  }, [
    isInitializingLivekit,
    sharingLink,
    name,
    email,
    sessionId,
    sessionToken,
    preparedSession,
    selectedMicrophone,
    navigate,
    state,
    interview,
    opening,
    maxQuestions,
    voiceModel,
    useEnhancedAI,
    deliveryMode,
  ]);

  const startCameraPreview = useCallback(async () => {
    try {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      }
      const constraints = { video: selectedCamera ? { deviceId: { exact: selectedCamera } } : true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      cameraStreamRef.current = stream;
      setCameraPermissionGranted(true);
      if (cameraPreviewRef.current) {
        cameraPreviewRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setCameraPermissionGranted(false);
    }
  }, [selectedCamera]);

  const stopCameraPreview = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
  }, []);

  // Load devices on mount
  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Early agent warm-up: call POST /session/prepare on mount so agent starts while user tests mic
  useEffect(() => {
    if (!sharingLink || prepareCalledRef.current) return;

    const canPrepareWithSession = sessionId && sessionToken;
    const canPrepareWithCredentials = name && email;

    if (!canPrepareWithSession && !canPrepareWithCredentials) return;

    prepareCalledRef.current = true;
    const headers = { 'Content-Type': 'application/json' };
    if (sessionToken) headers['x-session-token'] = sessionToken;

    const body = sessionId
      ? { sessionId, sharingLink: sharingLink || undefined }
      : { sharingLink, candidateName: name, candidateEmail: email };

    fetch(`${API_BASE_URL}/api/ai-interviews/session/prepare`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 503) {
            return res.json().then((data) => {
              if (data.code === 'CAPACITY_REACHED') {
                setCapacityAvailable(false);
                if (capacityRetryRef.current) clearTimeout(capacityRetryRef.current);
                capacityRetryRef.current = setTimeout(() => setCapacityAvailable(true), 30000);
              }
            });
          }
          return;
        }
        return res.json();
      })
      .then((data) => {
        if (data && data.sessionId) {
          setPreparedSession({
            sessionId: data.sessionId,
            sessionToken: data.sessionToken,
            maxQuestions: data.maxQuestions,
            voiceModel: data.voiceModel,
            useEnhancedAI: data.useEnhancedAI,
            deliveryMode: data.deliveryMode,
          });
        }
      })
      .catch(() => {
        // Prepare failed — fallback: /livekit-token will do full flow when user clicks Start
        prepareCalledRef.current = false;
      });
  }, [sharingLink, sessionId, sessionToken, name, email]);

  // Start input level monitoring when microphone is selected
  useEffect(() => {
    if (selectedMicrophone) {
      startInputLevelMonitoring();
    }
    return () => {
      stopInputLevelMonitoring();
    };
  }, [selectedMicrophone, startInputLevelMonitoring, stopInputLevelMonitoring]);

  // Cleanup capacity retry timeout on unmount
  useEffect(() => {
    return () => {
      if (capacityRetryRef.current) {
        clearTimeout(capacityRetryRef.current);
        capacityRetryRef.current = null;
      }
    };
  }, []);

  // Start camera preview automatically for video interviews
  useEffect(() => {
    if (isVideoInterview) {
      startCameraPreview();
    }
    return () => {
      stopCameraPreview();
    };
  }, [isVideoInterview, startCameraPreview, stopCameraPreview]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopInputLevelMonitoring();
      stopCameraPreview();
      if (testAudioRef.current) {
        testAudioRef.current.pause();
        testAudioRef.current = null;
      }
      if (roomRef.current && !roomRef.current.isConnected) {
        roomRef.current.disconnect();
      }
    };
  }, [stopInputLevelMonitoring, stopCameraPreview]);

  // Validate required state
  if (!sharingLink || !name || !email) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            {t('Missing required information. Please go back and try again.')}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            {t('Go Back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 sm:p-6">
      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={() => { setShowLeaveModal(false); navigate(-1); }}
        title={t('Leave this page?')}
        message={t('Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out.')}
      />
      <div className="w-full max-w-2xl">
        {/* Main Card - Ribbon.ai style */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6 sm:p-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  {t('Video and audio check')}
                </h1>
                <p className="text-sm text-gray-600">
                  {t('Check your setup before starting.')}
                </p>
              </div>
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <BellIcon className="w-5 h-5 text-purple-600" />
              </div>
            </div>

            {/* Audio Settings Section */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('Audio settings')}</h2>

              {/* Microphone */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Microphone')}</label>
                <select
                  value={selectedMicrophone || ''}
                  onChange={(e) => setSelectedMicrophone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {audioInputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || t('Microphone {id}', { id: device.deviceId.substring(0, 8) })}
                    </option>
                  ))}
                </select>

                {/* Input Level Indicator */}
                <div className="mt-3 flex gap-1 h-2">
                  {[...Array(10)].map((_, i) => {
                    const level = Math.floor(inputLevel * 10);
                    const isActive = i < level;
                    return (
                      <div
                        key={i}
                        className={`flex-1 rounded-full transition-all ${
                          isActive ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Speaker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Speaker')}</label>
                <div className="flex gap-3">
                  <select
                    value={selectedSpeaker || ''}
                    onChange={(e) => setSelectedSpeaker(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {audioOutputDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || t('Speaker {id}', { id: device.deviceId.substring(0, 8) })}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={testSpeaker}
                    disabled={isTestingSpeaker}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isTestingSpeaker ? t('Playing...') : t('Test speaker')}
                  </button>
                </div>
              </div>
            </div>

            {/* Video Settings Section */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('Video settings')}</h2>

              {isVideoInterview ? (
                <>
                  {/* Live Camera Preview */}
                  <div className="mb-4">
                    <div className="w-full aspect-video bg-gray-900 rounded-lg overflow-hidden mb-3 relative">
                      {cameraPermissionGranted ? (
                        <video
                          ref={cameraPreviewRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-cover"
                          style={{ transform: 'scaleX(-1)' }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <VideoCameraIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">{t('Camera access required')}</p>
                            <button
                              type="button"
                              onClick={startCameraPreview}
                              className="mt-2 px-4 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                            >
                              {t('Allow Camera')}
                            </button>
                          </div>
                        </div>
                      )}
                      {cameraPermissionGranted && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 bg-green-500/90 text-white text-xs font-medium rounded-full px-2 py-0.5">
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          {t('Camera ready')}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{t("Smile, you're on camera!")}</p>
                    <p className="text-xs text-gray-500">
                      {t('Center yourself in the frame. This interview will be recorded.')}
                    </p>
                  </div>

                  {/* Camera Selection */}
                  {videoDevices.length > 1 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('Camera')}</label>
                      <select
                        value={selectedCamera || ''}
                        onChange={(e) => setSelectedCamera(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        {videoDevices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || t('Camera {id}', { id: device.deviceId.slice(0, 8) })}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <div className="mb-4">
                  <div className="w-full aspect-video bg-gray-200 rounded-lg flex items-center justify-center mb-3">
                    <div className="text-center">
                      <VideoCameraIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">{t('Camera not required for voice interviews')}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('This is a voice-only interview. Audio will be recorded automatically.')}
                  </p>
                </div>
              )}
            </div>

            {/* Error Message */}
            {livekitError && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{t(livekitError)}</p>
              </div>
            )}

            {/* Capacity banner — shown when LiveKit dispatch is rejected */}
            {!capacityAvailable && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
                    <ClockIcon className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-amber-900 mb-1">
                      {t('All interview slots are currently in use')}
                    </h4>
                    <p className="text-sm text-amber-800">
                      {t('Please wait a few minutes and try again. The button will re-enable shortly.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Start Interview Button – brand gradient */}
            <button
              onClick={initializeLivekit}
              disabled={isInitializingLivekit || !selectedMicrophone || !capacityAvailable}
              className="w-full flex items-center justify-center px-6 py-3.5 bg-gradient-to-r from-electric-blue to-indigo-600 hover:from-electric-blue/90 hover:to-indigo-700 text-white text-base font-bold rounded-lg shadow-lg shadow-electric-blue/20 hover:shadow-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isInitializingLivekit ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  {t('Starting interview...')}
                </>
              ) : !capacityAvailable ? (
                t('Waiting for available slot...')
              ) : (
                t('Start interview')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIInterviewPreTest;
